import React, { useState, useEffect } from 'react';
import { Typography, Space, Button, Card, Form, Input, Select, Upload, DatePicker, Breadcrumb, Row, Col, Radio, Checkbox, Image, Spin, Alert } from 'antd';
import { UploadOutlined, DeleteOutlined, CloseOutlined, PlusOutlined, InfoCircleOutlined, CloseCircleOutlined, HolderOutlined, InboxOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { tenantService, Tenant } from '../services/tenants';
import noticeService from '../services/notices';
import { uploadService, UploadedFile } from '../services/upload';
import { convertImageToPNG, shouldConvertToPNG } from '../utils/imageConverter';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { showConfirmModal, getMessage } from '../components/PopupModal';
import { NoticeItem, NoticeContentItem } from '../types/notice';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';
import dayjs from 'dayjs';
import type { UploadFile, UploadProps } from 'antd';
import authService from '../services/auth';
import TenantPreviewModal from '../components/TenantPreviewModal';
import NoticePreviewModal from '../components/NoticePreviewModal';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

type ContentType = 'tenant' | 'notice';

interface SortableContentSectionProps {
  section: NoticeContentItem & { key: number };
  index: number;
  subtitleErrors: Record<number, string>;
  detailErrors: Record<number, string>;
  contentSections: (NoticeContentItem & { key: number })[];
  updateContentSection: (key: number, field: 'subtitle' | 'detail', value: string, element?: HTMLTextAreaElement) => void;
  removeContentSection: (key: number) => void;
}

const SortableContentSection: React.FC<SortableContentSectionProps> = ({
  section,
  index,
  subtitleErrors,
  detailErrors,
  contentSections,
  updateContentSection,
  removeContentSection,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: section.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: '16px',
    border: '1px solid #303030',
    borderRadius: '6px',
    padding: '16px',
    backgroundColor: '#1f1f1f',
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div {...attributes} {...listeners} style={{ cursor: 'grab', marginRight: '12px', paddingTop: '8px', display: 'flex', alignItems: 'center' }}>
          <HolderOutlined style={{ fontSize: '16px', color: '#8c8c8c' }} />
        </div>
        <div style={{ flex: 1, maxWidth: '58ch' }}>
          <Input
            placeholder="소제목을 입력하세요."
            value={section.subtitle}
            onChange={(e) => updateContentSection(section.key, 'subtitle', e.target.value)}
            maxLength={36}
            showCount
            status={subtitleErrors[section.key] ? 'error' : ''}
          />
          {subtitleErrors[section.key] && (
            <div style={{ marginTop: '4px' }}>
              <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
                {subtitleErrors[section.key]}
              </span>
            </div>
          )}
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={() => removeContentSection(section.key)}
          style={{ color: '#a6a6a6', marginLeft: 'auto' }}
          disabled={contentSections.length <= 1}
        />
      </div>
      <div style={{ marginLeft: '28px', marginBottom: '12px' }}>
        <TextArea
          rows={2}
          placeholder="내용을 입력하세요."
          value={section.detail}
          onChange={(e) => updateContentSection(section.key, 'detail', e.target.value, e.target as HTMLTextAreaElement)}
          onKeyDown={(e) => {
            const value = (e.target as HTMLTextAreaElement).value;
            const lineCount = (value.match(/\n/g) || []).length;
            if (e.key === 'Enter' && lineCount >= 1) {
              e.preventDefault();
              getMessage().warning('최대 2줄까지만 입력 가능합니다.');
            }
          }}
          style={{ maxWidth: '42ch', resize: 'none' }}
          maxLength={54}
          showCount
          status={detailErrors[section.key] ? 'error' : ''}
        />
        {detailErrors[section.key] && (
          <div style={{ marginTop: '4px' }}>
            <span style={{ fontSize: '12px', color: '#ff4d4f' }}>
              {detailErrors[section.key]}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const ContentApprovalEdit: React.FC = () => {
  const navigate = useNavigate();
  const { clearAllBlockers } = useNavigationBlockerContext();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const contentType = (searchParams.get('type') || 'tenant') as ContentType;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Tenant 관련 상태
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isEventContent, setIsEventContent] = useState(false);
  const [attachedFileList, setAttachedFileList] = useState<UploadFile[]>([]);
  const [additionalFileList, setAdditionalFileList] = useState<UploadFile[]>([]);
  const [uploadedAttachedFile, setUploadedAttachedFile] = useState<UploadedFile | null>(null);
  const [uploadedAdditionalFile, setUploadedAdditionalFile] = useState<UploadedFile | null>(null);
  const [isMainImageDeleted, setIsMainImageDeleted] = useState(false);
  const [isThumbnailDeleted, setIsThumbnailDeleted] = useState(false);

  // Notice 관련 상태
  const [notice, setNotice] = useState<NoticeItem | null>(null);
  const [contentSections, setContentSections] = useState<(NoticeContentItem & { key: number })[]>([]);
  const [titleError, setTitleError] = useState<string>('');
  const [subtitleErrors, setSubtitleErrors] = useState<Record<number, string>>({});
  const [detailErrors, setDetailErrors] = useState<Record<number, string>>({});
  const [additionalInfoError, setAdditionalInfoError] = useState<string>('');
  const [isAlwaysVisible, setIsAlwaysVisible] = useState(false);

  // 승인 상태 관련
  const [approvalStatus, setApprovalStatus] = useState<'pending' | 'approved' | 'rejected'>('pending');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);

  // 미리보기 모달 상태
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
  const [isModified, setIsModified] = useState(false);

  // 네비게이션 가드 적용
  useNavigationBlocker(isModified, '/content-approval-management');

  useEffect(() => {
    if (id) {
      loadContentData();
    }
    loadCompanyNames();
  }, [id, contentType]);

  // 입주사 목록 불러오기
  const loadCompanyNames = async () => {
    try {
      const response = await tenantService.getTenants({ page: 1, limit: 1000 });
      const options = response.data.tenants.map((tenant: any) => ({
        value: tenant.name,
        label: tenant.name
      }));
      setCompanyOptions(options);
    } catch (error) {
      console.error('Failed to load company names:', error);
    }
  };

  const loadContentData = async () => {
    try {
      setInitialLoading(true);
      if (contentType === 'tenant') {
        const response = await tenantService.getTenant(Number(id));
        const tenantData = response.data;
        setTenant(tenantData);
        setIsEventContent(tenantData.isEventContent || false);
        setApprovalStatus(tenantData.approvalStatus || 'pending');
        setRejectionReason(tenantData.rejectionReason || '');

        form.setFieldsValue({
          layout: tenantData.layout || 'layout1',
          exposure: tenantData.status === 'active' ? 'show' : 'hide',
          companyName: tenantData.name || '',
          phoneNumber: tenantData.phone || '',
          content: tenantData.description || '',
        });
      } else {
        const noticeData = await noticeService.getNoticeDetail(Number(id));
        setNotice(noticeData);
        setApprovalStatus(noticeData.approvalStatus || 'pending');
        setRejectionReason(noticeData.rejectionReason || '');

        // 상시 노출 여부 확인 (endDate가 50년 이상 미래이면 상시 노출)
        const checkAlwaysVisible = (endDate: string): boolean => {
          try {
            const end = new Date(endDate);
            const now = new Date();
            const fiftyYearsFromNow = new Date();
            fiftyYearsFromNow.setFullYear(now.getFullYear() + 50);
            return end >= fiftyYearsFromNow;
          } catch {
            return false;
          }
        };

        const alwaysVisible = checkAlwaysVisible(noticeData.endDate);
        setIsAlwaysVisible(alwaysVisible);

        form.setFieldsValue({
          layout: noticeData.layout,
          exposure: noticeData.exposure,
          exposurePeriod: alwaysVisible ? null : [dayjs(noticeData.startDate), dayjs(noticeData.endDate)],
          title: noticeData.title,
          additionalInfo: noticeData.additionalInfo || ''
        });

        const sectionsWithKeys = noticeData.contents.map((content, index) => ({
          key: index + 1,
          subtitle: content.subtitle,
          detail: content.detail
        }));
        setContentSections(sectionsWithKeys);
      }
    } catch (error) {
      console.error('Failed to load content data:', error);
      getMessage().error('데이터를 불러오는데 실패했습니다.');
      navigate('/content-approval');
    } finally {
      setInitialLoading(false);
    }
  };

  // Tenant 파일 업로드 핸들러
  const handleAttachedFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    try {
      let fileToUpload = file as File;

      // PNG 변환이 필요한 파일인지 확인
      if (shouldConvertToPNG(fileToUpload)) {
        try {
          fileToUpload = await convertImageToPNG(fileToUpload);
        } catch (error) {
          console.error('이미지 PNG 변환 실패:', error);
          // 변환 실패 시 원본 파일로 계속 진행
        }
      }

      const validation = uploadService.validateFile(fileToUpload);
      if (!validation.valid) {
        getMessage().error(validation.error);
        onError?.(new Error(validation.error!));
        return;
      }
      const response = await uploadService.uploadSingleImage(fileToUpload);
      if (response.success) {
        setUploadedAttachedFile(response.data);
        setIsMainImageDeleted(false);
        getMessage().success('파일이 성공적으로 업로드되었습니다.');
        onSuccess?.(response.data);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      getMessage().error('파일 업로드에 실패했습니다.');
      onError?.(error);
    }
  };

  const handleAdditionalFileUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    try {
      let fileToUpload = file as File;

      // PNG 변환이 필요한 파일인지 확인
      if (shouldConvertToPNG(fileToUpload)) {
        try {
          fileToUpload = await convertImageToPNG(fileToUpload);
        } catch (error) {
          console.error('이미지 PNG 변환 실패:', error);
          // 변환 실패 시 원본 파일로 계속 진행
        }
      }

      const validation = uploadService.validateFile(fileToUpload);
      if (!validation.valid) {
        getMessage().error(validation.error);
        onError?.(new Error(validation.error!));
        return;
      }
      const response = await uploadService.uploadSingleImage(fileToUpload);
      if (response.success) {
        setUploadedAdditionalFile(response.data);
        setIsThumbnailDeleted(false);
        getMessage().success('파일이 성공적으로 업로드되었습니다.');
        onSuccess?.(response.data);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      getMessage().error('파일 업로드에 실패했습니다.');
      onError?.(error);
    }
  };

  const handleRemoveAttachedFile = () => {
    setAttachedFileList([]);
    setUploadedAttachedFile(null);
    setIsMainImageDeleted(true);
    if (tenant) {
      setTenant({ ...tenant, imageUrl: undefined });
    }
  };

  const handleRemoveAdditionalFile = () => {
    setAdditionalFileList([]);
    setUploadedAdditionalFile(null);
    setIsThumbnailDeleted(true);
    if (tenant) {
      setTenant({ ...tenant, thumbnailUrl: undefined });
    }
  };

  // 대표번호 검증 핸들러
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // '-' 포함 여부 확인
    if (value.includes('-')) {
      setPhoneError("'-'를 제외하고 입력해주세요.");
    } else if (value && !/^\d*$/.test(value)) {
      // 숫자가 아닌 문자 확인
      setPhoneError('숫자만 입력 가능합니다.');
    } else {
      setPhoneError('');
    }

    // 폼 값 업데이트
    form.setFieldsValue({ phoneNumber: value });
  };

  // 자동 줄바꿈 삽입 함수
  const insertAutoLineBreaks = (text: string, element: HTMLTextAreaElement, maxWidth: number): string => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return text;

    const computedStyle = window.getComputedStyle(element);
    const font = `${computedStyle.fontSize} ${computedStyle.fontFamily}`;
    context.font = font;

    const lines: string[] = [];
    const paragraphs = text.split('\n');

    paragraphs.forEach((paragraph, pIndex) => {
      if (paragraph === '') {
        if (pIndex < paragraphs.length - 1) {
          lines.push('');
        }
        return;
      }

      let currentLine = '';
      const words = paragraph.split('');

      words.forEach((char) => {
        const testLine = currentLine + char;
        const metrics = context.measureText(testLine);

        if (metrics.width > maxWidth && currentLine !== '') {
          lines.push(currentLine);
          currentLine = char;
        } else {
          currentLine = testLine;
        }
      });

      if (currentLine) {
        lines.push(currentLine);
      }
    });

    return lines.join('\n');
  };

  // Notice 제목 변경 핸들러
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value || value.trim() === '') {
      setTitleError('제목을 입력해주세요.');
    } else if (value.length > 17) {
      setTitleError('제목은 17자를 초과할 수 없습니다.');
    } else {
      setTitleError('');
    }
    form.setFieldsValue({ title: value });
  };

  // Notice 추가 안내 변경 핸들러
  const handleAdditionalInfoChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let value = e.target.value;
    const element = e.target;

    // 58ch 너비 기준으로 자동 줄바꿈 처리
    const computedStyle = window.getComputedStyle(element);
    const fontSize = parseFloat(computedStyle.fontSize);
    const maxWidth = fontSize * 58;
    value = insertAutoLineBreaks(value, element, maxWidth);

    // 3줄 제한
    const lineCount = (value.match(/\n/g) || []).length;
    if (lineCount > 2) {
      const limitedLines = value.split('\n').slice(0, 3);
      value = limitedLines.join('\n');
    }

    // 유효성 검증
    if (value.length > 120) {
      setAdditionalInfoError('추가 안내는 120자를 초과할 수 없습니다.');
    } else {
      setAdditionalInfoError('');
    }

    form.setFieldsValue({ additionalInfo: value });
  };

  // Notice 컨텐츠 섹션 관리
  const addContentSection = () => {
    if (contentSections.length >= 3) {
      getMessage().warning('내용은 최대 3개까지 추가할 수 있습니다.');
      return;
    }
    const newKey = contentSections.length > 0 ? Math.max(...contentSections.map(s => s.key)) + 1 : 1;
    setContentSections(prev => [...prev, { key: newKey, subtitle: '', detail: '' }]);
  };

  const removeContentSection = (key: number) => {
    setContentSections(prev => prev.filter(section => section.key !== key));
  };

  const updateContentSection = (key: number, field: 'subtitle' | 'detail', value: string, element?: HTMLTextAreaElement) => {
    // 필수 입력 및 글자수 체크
    if (field === 'subtitle') {
      if (!value || value.trim() === '') {
        setSubtitleErrors(prev => ({ ...prev, [key]: '소제목을 입력해주세요.' }));
      } else if (value.length > 36) {
        setSubtitleErrors(prev => ({ ...prev, [key]: '소제목은 36자를 초과할 수 없습니다.' }));
      } else {
        setSubtitleErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[key];
          return newErrors;
        });
      }
    } else if (field === 'detail') {
      // 58ch 너비 기준으로 자동 줄바꿈 처리
      if (element) {
        const computedStyle = window.getComputedStyle(element);
        const fontSize = parseFloat(computedStyle.fontSize);
        const maxWidth = fontSize * 58;
        value = insertAutoLineBreaks(value, element, maxWidth);
      }

      // 2줄 제한
      const lineCount = (value.match(/\n/g) || []).length;
      if (lineCount > 1) {
        const limitedLines = value.split('\n').slice(0, 2);
        value = limitedLines.join('\n');
      }

      if (!value || value.trim() === '') {
        setDetailErrors(prev => ({ ...prev, [key]: '세부정보를 입력해주세요.' }));
      } else if (value.length > 54) {
        setDetailErrors(prev => ({ ...prev, [key]: '세부정보는 54자를 초과할 수 없습니다.' }));
      } else {
        setDetailErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[key];
          return newErrors;
        });
      }
    }

    setIsModified(true);
    setContentSections(prev => prev.map(section =>
      section.key === key ? { ...section, [field]: value } : section
    ));
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setContentSections((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // 저장 핸들러
  const handleSave = async () => {
    try {
      // 대표번호 에러 체크 (입주사 타입인 경우)
      if (contentType === 'tenant' && phoneError) {
        getMessage().error(phoneError);
        return;
      }

      const values = await form.validateFields();

      // 승인대기 상태일 때 자동 승인 확인, 다른 상태일 때는 일반 저장 확인
      const modalContent = approvalStatus === 'pending'
        ? '저장 시 콘텐츠가 자동 승인됩니다.\n저장하시겠습니까?'
        : '저장 시 콘텐츠가 자동 승인됩니다.\n저장하시겠습니까?';

      const autoApprove = approvalStatus === 'pending';

      showConfirmModal({
        title: '알림',
        content: modalContent,
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => performSave(values, autoApprove),
      });
    } catch (error: any) {
      if (error?.errorFields) {
        getMessage().error('필수 입력 항목을 확인해주세요.');
      } else {
        console.error('Validation error:', error);
      }
    }
  };

  // 실제 저장 로직
  const performSave = async (values: any, autoApprove: boolean) => {
    try {
      setLoading(true);

      if (contentType === 'tenant') {
        if (isMainImageDeleted || (!uploadedAttachedFile && !tenant?.imageUrl)) {
          getMessage().error('입주사 이미지를 첨부해주세요.');
          setLoading(false);
          return;
        }

        if (isEventContent && (isThumbnailDeleted || (!uploadedAdditionalFile && !tenant?.thumbnailUrl))) {
          getMessage().error('이벤트 콘텐츠 첨부파일을 업로드해주세요.');
          setLoading(false);
          return;
        }

        const updateData = {
          name: values.companyName,
          phone: values.phoneNumber,
          description: values.content,
          layout: values.layout,
          isEventContent: isEventContent,
          status: values.exposure === 'show' ? 'active' as const : 'inactive' as const,
          imageUrl: isMainImageDeleted ? undefined : (uploadedAttachedFile?.url || tenant?.imageUrl),
          thumbnailUrl: isThumbnailDeleted ? undefined : (uploadedAdditionalFile?.url || tenant?.thumbnailUrl),
        };

        await tenantService.updateTenant(Number(id), updateData);

        // 자동 승인 처리
        if (autoApprove) {
          await tenantService.approveTenant(Number(id), 'approved');
        }

        getMessage().success('승인 되었습니다.');
      } else {
        // Notice 검증
        if (!values.title || values.title.trim() === '') {
          setTitleError('제목을 입력해주세요.');
          getMessage().error('필수 항목을 모두 입력해주세요.');
          setLoading(false);
          return;
        }

        if (contentSections.length === 0) {
          getMessage().error('내용을 최소 1개 이상 입력해주세요.');
          setLoading(false);
          return;
        }

        let hasEmptyFields = false;
        const newSubtitleErrors: Record<number, string> = {};
        const newDetailErrors: Record<number, string> = {};

        contentSections.forEach(section => {
          if (!section.subtitle || section.subtitle.trim() === '') {
            newSubtitleErrors[section.key] = '소제목을 입력해주세요.';
            hasEmptyFields = true;
          }
          if (!section.detail || section.detail.trim() === '') {
            newDetailErrors[section.key] = '세부정보를 입력해주세요.';
            hasEmptyFields = true;
          }
        });

        if (hasEmptyFields) {
          setSubtitleErrors(newSubtitleErrors);
          setDetailErrors(newDetailErrors);
          getMessage().error('필수 항목을 모두 입력해주세요.');
          setLoading(false);
          return;
        }

        const updateData = {
          id: Number(id),
          title: values.title,
          layout: values.layout,
          exposure: values.exposure,
          startDate: values.exposurePeriod[0].format('YYYY-MM-DD'),
          endDate: values.exposurePeriod[1].format('YYYY-MM-DD'),
          contents: contentSections.map(({ key, ...content }) => content),
          additionalInfo: values.additionalInfo || undefined
        };

        await noticeService.updateNotice(updateData);

        // 자동 승인 처리
        if (autoApprove) {
          await noticeService.approveNotice(Number(id), 'approved');
        }

        getMessage().success('승인 되었습니다.');
      }

      clearAllBlockers();
      navigate(`/content-approval/detail/${id}?type=${contentType}`);
    } catch (error) {
      console.error('Save error:', error);
      getMessage().error('저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (isModified) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          setIsModified(false);
          setTimeout(() => {
            navigate(`/content-approval/detail/${id}?type=${contentType}`);
          }, 0);
        },
      });
    } else {
      navigate(`/content-approval/detail/${id}?type=${contentType}`);
    }
  };

  // 미리보기용 데이터 준비
  const getPreviewData = () => {
    const formValues = form.getFieldsValue();

    if (contentType === 'tenant') {
      // 입주사 미리보기 데이터
      const previewTenant: Tenant = {
        id: tenant?.id || 0,
        name: formValues.companyName || '',
        phone: formValues.phoneNumber || '',
        description: formValues.content || '',
        layout: formValues.layout || 'layout1',
        isEventContent: isEventContent,
        status: formValues.exposure === 'show' ? 'active' : 'inactive',
        approvalStatus: approvalStatus,
        imageUrl: uploadedAttachedFile?.url || tenant?.imageUrl,
        thumbnailUrl: uploadedAdditionalFile?.url || tenant?.thumbnailUrl,
        createdAt: tenant?.createdAt || new Date().toISOString(),
        updatedAt: tenant?.updatedAt || new Date().toISOString(),
        tenantUnits: tenant?.tenantUnits, // 입주사 관리의 대표 이미지 정보 포함
      };
      return previewTenant;
    } else {
      // 공지사항 미리보기 데이터
      const previewNotice: NoticeItem = {
        id: Number(id) || 0,
        title: formValues.title || '',
        layout: formValues.layout || '1',
        exposure: formValues.exposure || 'show',
        startDate: formValues.exposurePeriod?.[0]?.format('YYYY-MM-DD') || '',
        endDate: formValues.exposurePeriod?.[1]?.format('YYYY-MM-DD') || '',
        contents: contentSections.map(({ key, ...content }) => content),
        additionalInfo: formValues.additionalInfo,
        approvalStatus: approvalStatus,
        createdBy: 0,
        createdAt: notice?.createdAt || new Date().toISOString(),
        updatedAt: notice?.updatedAt || new Date().toISOString(),
      };
      return previewNotice;
    }
  };

  // 승인 상태 알림 렌더링
  const renderApprovalAlert = () => {
    let alertMessage = '';
    let alertType: 'info' | 'warning' | 'error' = 'info';
    let icon = <InfoCircleOutlined />;

    const isSuperAdmin = authService.isSuperAdmin();

    switch (approvalStatus) {
      case 'pending':
        if (isSuperAdmin) {
          alertMessage = '승인이 필요한 콘텐츠입니다. 검토 후 승인 여부를 진행해주세요.';
          alertType = 'info';
        } else {
          alertMessage = '승인 대기중인 콘텐츠입니다. 수정 및 삭제가 불가합니다.';
          alertType = 'warning';
        }
        icon = <InfoCircleOutlined />;
        break;
      case 'approved':
        alertMessage = '승인 완료된 콘텐츠입니다.';
        alertType = 'info';
        icon = <InfoCircleOutlined />;
        break;
      case 'rejected':
        alertMessage = rejectionReason
          ? `승인 거부된 콘텐츠입니다.\n사유 : ${rejectionReason}`
          : '승인 거부된 콘텐츠입니다.';
        alertType = 'error';
        icon = <CloseCircleOutlined />;
        break;
    }

    return (
      <Alert
        message={alertMessage}
        type={alertType}
        icon={icon}
        showIcon
        style={{ whiteSpace: 'pre-line', marginBottom: '24px' }}
      />
    );
  };

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '콘텐츠 승인 관리', href: '/content-approval' },
          { title: contentType === 'tenant' ? '입주사 소개 수정' : '공지사항 수정' }
        ]}
        style={{ marginBottom: '8px' }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={2} style={{ margin: 0 }}>
          {contentType === 'tenant' ? '입주사 소개 수정' : '공지사항 수정'}
        </Title>
        <Button onClick={() => setIsPreviewModalVisible(true)}>미리보기</Button>
      </div>

      {renderApprovalAlert()}

      <Form form={form} layout="vertical" requiredMark={false} style={{ width: '100%' }}
        onValuesChange={() => setIsModified(true)}
      >
        {contentType === 'tenant' ? (
          <>
            {/* 입주사 폼 */}
            <Card title="기본 정보" style={{ marginBottom: '24px' }}>
              <Row gutter={[24, 16]}>
                <Col span={6}>
                  <Form.Item label="레이아웃명" name="layout">
                    <Select disabled allowClear={false}>
                      <Option value="layout1">레이아웃 1 (5개 구성)</Option>
                      <Option value="layout2">레이아웃 2 (4개 구성)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[24, 16]} style={{ height: '54px' }}>
                <Col span={6}>
                  <Form.Item label="노출여부" name="exposure">
                    <Radio.Group>
                      <Radio value="show">노출</Radio>
                      <Radio value="hide">비노출</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="입주사 정보" style={{ marginBottom: '24px' }}>
              <Row gutter={[24, 16]} style={{ alignItems: 'flex-end', paddingBottom: '20px' }}>
                <Col span={6}>
                  <Form.Item
                    label={<Text>입주사명 <span style={{ color: 'red' }}>*</span></Text>}
                    name="companyName"
                    required={false}
                    rules={[{ required: true, message: '입주사명을 입력해주세요.' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      placeholder="회사명을 입력하세요."
                      allowClear
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={companyOptions}
                      style={{ width: '100%' }}
                      notFoundContent="등록된 입주사가 없습니다."
                    />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                    <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                    <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                      입주사 관리에 등록된 입주사명만 검색됩니다.
                    </Text>
                  </div>
                </Col>
              </Row>
              <Row gutter={[24, 16]} style={{ alignItems: 'flex-end' }}>
                <Col span={6}>
                  <Form.Item
                    label="대표번호"
                    name="phoneNumber"
                    validateStatus={phoneError ? 'error' : ''}
                    help={phoneError ? <span style={{ fontSize: '12px', color: '#ff4d4f' }}>{phoneError}</span> : ''}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="대표번호 입력" onChange={handlePhoneChange} />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  {!phoneError && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                      <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                      <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                        '-' 를 제외하고 입력해주세요.
                      </Text>
                    </div>
                  )}
                </Col>
              </Row>
            </Card>

            <Card title="입주사 설명" style={{ marginBottom: '24px' }}>
              <Form.Item
                label={<Text>내용 <span style={{ color: 'red' }}>*</span></Text>}
                name="content"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.reject(new Error('내용을 입력해주세요.'));
                      }
                      if (value.length > 123) {
                        return Promise.reject(new Error('내용은 123자 이내로 입력해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  maxLength={123}
                  showCount
                  placeholder="입주사 소개 내용 입력"
                  style={{
                    maxWidth: '58ch',
                    resize: 'none',
                    whiteSpace: 'pre-wrap'
                  }}
                />
              </Form.Item>
            </Card>

            <Card title="입주사 이미지" style={{ marginBottom: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <Text style={{ display: 'block', marginBottom: '8px', color: '#ffffff' }}>
                  <span>첨부파일 <span style={{ color: 'red' }}>*</span></span>
                </Text>

                {/* 기존 이미지 또는 새로 업로드된 이미지 미리보기 */}
                {(uploadedAttachedFile || tenant?.imageUrl) && !isMainImageDeleted ? (
                  <div style={{ marginBottom: '16px', border: '1px solid #303030', borderRadius: '8px', padding: '16px', backgroundColor: '#1a1a1a' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <Text style={{ color: '#ffffff' }}>
                        {uploadedAttachedFile ? '새로 업로드된 파일' : '기존 파일'}
                      </Text>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={handleRemoveAttachedFile}
                        style={{ color: '#ff4d4f' }}
                      >
                        삭제
                      </Button>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <Image
                        width={100}
                        height={100}
                        src={uploadedAttachedFile?.url || tenant?.imageUrl}
                        alt="입주사 이미지"
                        style={{ objectFit: 'cover', borderRadius: '4px' }}
                        preview={{
                          mask: <div><EyeOutlined /> 미리보기</div>
                        }}
                      />
                      <div>
                        <Text style={{ color: '#ffffff', display: 'block' }}>
                          {uploadedAttachedFile?.originalName || '입주사 대표 이미지'}
                        </Text>
                        <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                          {uploadedAttachedFile ?
                            `크기: ${uploadService.formatFileSize(uploadedAttachedFile.size)}` :
                            '기존 등록 이미지'
                          }
                        </Text>
                      </div>
                    </div>
                  </div>
                ) : null}

                <Dragger
                  fileList={attachedFileList}
                  onChange={({ fileList }) => setAttachedFileList(fileList)}
                  customRequest={handleAttachedFileUpload}
                  accept="image/*"
                  maxCount={1}
                  showUploadList={false}
                  disabled={!!(uploadedAttachedFile || (tenant?.imageUrl && !isMainImageDeleted))}
                  style={{
                    background: '#141414',
                    border: '2px dashed #303030',
                    borderRadius: '8px'
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: '#a6a6a6', fontSize: '48px' }} />
                  </p>
                  <p style={{ color: '#ffffff', fontSize: '16px', margin: '16px 0 8px' }}>
                    Upload
                  </p>
                </Dragger>
              </div>
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                    파일형식: PNG, JPEG, GIF, WEBP / 50MB 이하 (최대 1개)
                  </Text>
                </div>
                <div style={{ marginLeft: '24px' }}>
                  <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                    권장 이미지 크기: 800px(가로) × 600px(세로) 이상
                  </Text>
                </div>
              </div>
            </Card>

            <Card title="기타 정보" style={{ marginBottom: '24px' }}>
              <Form.Item label="이벤트 콘텐츠">
                <Radio.Group value={isEventContent} onChange={(e) => setIsEventContent(e.target.value)}>
                  <Radio value={true}>Y</Radio>
                  <Radio value={false}>N</Radio>
                </Radio.Group>
              </Form.Item>

              {isEventContent && (
                <div style={{ marginTop: '24px' }}>
                  <Text style={{ display: 'block', marginBottom: '16px' }}>이벤트 첨부파일 <span style={{ color: '#ff4d4f' }}>*</span></Text>
                  {tenant?.thumbnailUrl && !isThumbnailDeleted && !uploadedAdditionalFile ? (
                    <div style={{ marginBottom: '16px' }}>
                      <Image width={120} height={90} src={tenant.thumbnailUrl} style={{ objectFit: 'cover', borderRadius: '4px' }} />
                      <Button danger icon={<DeleteOutlined />} onClick={handleRemoveAdditionalFile} style={{ marginLeft: '8px' }}>삭제</Button>
                    </div>
                  ) : null}
                  <Upload
                    customRequest={handleAdditionalFileUpload}
                    fileList={additionalFileList}
                    onChange={({ fileList }) => setAdditionalFileList(fileList)}
                    maxCount={1}
                    listType="picture"
                  >
                    <Button icon={<UploadOutlined />}>파일 선택</Button>
                  </Upload>
                </div>
              )}
            </Card>
          </>
        ) : (
          <>
            {/* 공지사항 폼 */}
            <Card title="기본 정보" style={{ marginBottom: '24px' }}>
              <Row gutter={[24, 16]}>
                <Col span={6}>
                  <Form.Item label="레이아웃명" name="layout">
                    <Select disabled allowClear={false}>
                      <Option value="layout1">레이아웃 1 (5개 구성)</Option>
                      <Option value="layout2">레이아웃 2 (4개 구성)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[24, 16]}>
                <Col span={6}>
                  <Form.Item
                    label={<span>노출여부 <span style={{ color: 'red' }}>*</span></span>}
                    name="exposure"
                    rules={[
                      { required: false, message: '노출 여부를 선택해주세요.' }
                    ]}
                  >
                    <Radio.Group>
                      <Radio value="show">노출</Radio>
                      <Radio value="hide">비노출</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[24, 16]} style={{ alignItems: 'flex-end' }}>
                <Col span={6}>
                  <Form.Item
                    label={<span>노출 기간 <span style={{ color: 'red' }}>*</span></span>}
                    name="exposurePeriod"
                    rules={[
                      { required: false, message: '노출 기간을 선택해주세요.' }
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <RangePicker
                      format="YYYY.MM.DD"
                      disabled={isAlwaysVisible}
                      style={{ width: '100%', background: '#141414', border: '1px solid #303030' }}
                    />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <div style={{ paddingBottom: '8px' }}>
                    <Checkbox
                      checked={isAlwaysVisible}
                      onChange={(e: any) => {
                        setIsAlwaysVisible(e.target.checked);
                        if (e.target.checked) {
                          form.setFieldsValue({ exposurePeriod: null });
                        }
                      }}
                    >
                      상시 노출
                    </Checkbox>
                  </div>
                </Col>
                <Col span={16}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px' }}>
                    <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                    <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                      기간이 종료되면 자동 비공개 처리됩니다.
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>

            <Card title="상세 내용" style={{ marginBottom: '24px' }}>
              <Form.Item
                label={<span>제목 <span style={{ color: 'red' }}>*</span></span>}
                name="title"
                validateStatus={titleError ? 'error' : ''}
                help={titleError ? <span style={{ fontSize: '12px', color: '#ff4d4f' }}>{titleError}</span> : ''}
                rules={[
                  { required: false, message: '제목을 입력해주세요.' },
                  { max: 17, message: '제목은 17자를 초과할 수 없습니다.' }
                ]}
                style={{ marginBottom: '24px' }}
              >
                <Input
                  placeholder="공지사항 제목을 입력하세요"
                  maxLength={17}
                  showCount
                  onChange={handleTitleChange}
                />
              </Form.Item>

              <Form.Item
                label={<span>내용 <span style={{ color: 'red' }}>*</span></span>}
                style={{ marginBottom: '16px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px' }} />
                  <Text style={{ fontSize: '12px', color: '#a6a6a6' }}>
                    소제목과 세부정보를 한 묶음으로 하여 여러 개의 내용을 추가할 수 있습니다.
                  </Text>
                </div>

                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={contentSections.map(s => s.key)} strategy={verticalListSortingStrategy}>
                    {contentSections.map((section, index) => (
                      <SortableContentSection
                        key={section.key}
                        section={section}
                        index={index}
                        subtitleErrors={subtitleErrors}
                        detailErrors={detailErrors}
                        contentSections={contentSections}
                        updateContentSection={updateContentSection}
                        removeContentSection={removeContentSection}
                      />
                    ))}
                  </SortableContext>
                </DndContext>

                <div style={{
                  border: contentSections.length >= 3 ? '1px dashed #595959' : '1px dashed #F24822',
                  borderRadius: '6px',
                  padding: '16px',
                  textAlign: 'center'
                }}>
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={addContentSection}
                    style={{
                      color: contentSections.length >= 3 ? '#595959' : '#F24822',
                      cursor: contentSections.length >= 3 ? 'not-allowed' : 'pointer'
                    }}
                    disabled={contentSections.length >= 3}
                  >
                    내용 추가 ({contentSections.length}/3)
                  </Button>
                </div>
              </Form.Item>
            </Card>

            <Card title="기타 정보" style={{ marginBottom: '32px' }}>
              <Form.Item
                label="추가 안내"
                name="additionalInfo"
                validateStatus={additionalInfoError ? 'error' : ''}
                help={additionalInfoError ? <span style={{ fontSize: '12px', color: '#ff4d4f' }}>{additionalInfoError}</span> : ''}
              >
                <TextArea
                  rows={3}
                  showCount
                  maxLength={120}
                  placeholder="추가적인 안내사항이 있다면 입력해주세요."
                  onChange={handleAdditionalInfoChange}
                  onKeyDown={(e) => {
                    const value = (e.target as HTMLTextAreaElement).value;
                    const lineCount = (value.match(/\n/g) || []).length;
                    // Enter 키를 누르고 이미 2개의 줄바꿈이 있으면 (총 3줄) 입력 방지
                    if (e.key === 'Enter' && lineCount >= 2) {
                      e.preventDefault();
                      getMessage().warning('최대 3줄까지만 입력 가능합니다.');
                    }
                  }}
                  style={{ maxWidth: '58ch', resize: 'none' }}
                />
              </Form.Item>
            </Card>
          </>
        )}

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '32px' }}>
          <Button size="large" onClick={handleCancel} style={{ height: 48 }}>
            취소
          </Button>
          <Button type="primary" size="large" onClick={handleSave} loading={loading} style={{ height: 48 }}>
            저장
          </Button>
        </div>
      </Form>

      {/* 미리보기 모달 */}
      {contentType === 'tenant' ? (
        <TenantPreviewModal
          visible={isPreviewModalVisible}
          onClose={() => setIsPreviewModalVisible(false)}
          tenant={getPreviewData() as Tenant}
        />
      ) : (
        <NoticePreviewModal
          visible={isPreviewModalVisible}
          onClose={() => setIsPreviewModalVisible(false)}
          notice={getPreviewData() as NoticeItem}
        />
      )}
    </Space>
  );
};

export default ContentApprovalEdit;
